import { useMemo, useEffect, useState } from 'react';
import {
  IconButton,
  ButtonGroup,
  InputGroup,
  Input,
  InputRightElement,
  Box,
  Grid,
  GridItem,
  Flex,
  Button,
  Tooltip,
  Select,
  useToast,
  Text,
} from '@chakra-ui/react';
import shallow from 'zustand/shallow';
import { MdDelete, MdEdit, MdSearch } from 'react-icons/md';

import {
  Pagination,
  DataTable,
  ErrorAlert,
  DeleteModal,
} from '../../../../common/components';
import { formatFullDate } from '../../../../lib/helper';
import { useDeleteAcademicYears, useListAcademicYear } from '../context/hooks';
import { useAcademicYear } from '../context';

function AcademicYearTable() {
  const toast = useToast();
  const [limit, setLimit] = useState(10);
  const [del, setDel] = useState(null);
  const [remove, statusDelete] = useDeleteAcademicYears();
  const [keywords, setKeywords] = useAcademicYear(
    (state) => [state.keywords, state.setKeywords],
    shallow,
  );

  const [page, setPage] = useAcademicYear(
    (state) => [state.page, state.setPage],
    shallow,
  );

  const [result] = useAcademicYear((state) => [state.result], shallow);

  const [setSelected, setCreateModal] = useAcademicYear(
    (state) => [state.setSelected, state.setCreateModal],
    shallow,
  );

  const {
    status,
    data: response,
    errorMessage,
    refetch,
    isFetching,
  } = useListAcademicYear({ keywords, page, limit });

  useEffect(() => {
    refetch();
  }, [result, page, limit]);

  const handleDelete = async () => {
    try {
      await remove(del.id);
      toast({
        title: 'Berhasil',
        description: 'Berhasil menghapus tahun ajaran',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Gagal',
        description: 'Gagal menghapus tahun ajaran',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    refetch();

    setDel(null);
  };
  const columns = useMemo(
    () => [
      {
        Header: 'Tahun',
        accessor: (row) => row.years.split('/').join(' / '),
      },
      {
        Header: 'Semester',
        accessor: (row) => row.semester.toUpperCase(),
      },
      {
        Header: 'Periode',
        accessor: (row) =>
          `${formatFullDate(row.startSemester)} - ${formatFullDate(
            row.endSemester,
          )}`,
      },
      {
        Header: 'Deskripsi',
        accessor: 'description',
      },
      {
        Header: 'Aksi',
        accessor: (row) => (
          <ButtonGroup size="sm" isAttached variant="outline">
            <Tooltip label="Edit Tahun Ajaran">
              <IconButton onClick={() => setSelected(row)} icon={<MdEdit />} />
            </Tooltip>
            {/* <Tooltip label="Hapus Tahun Ajaran">
              <IconButton icon={<MdDelete />} onClick={() => setDel(row)} />
            </Tooltip> */}
          </ButtonGroup>
        ),
      },
    ],
    [],
  );

  return (
    <Box mt={3}>
      <DeleteModal
        content={`Tahun Ajaran Ini`}
        isOpen={!!del}
        onClose={() => setDel(null)}
        onConfirm={() => handleDelete()}
        isLoading={statusDelete == 'fetching'}
      />
      <Grid
        templateColumns="repeat(8, 1fr)"
        justifyContent="space-between"
        gap={4}
      >
        <GridItem colSpan={{ base: 8, md: 4 }}>
          <Flex>
            {/*<InputGroup >
            <Input
              placeholder="Cari Tahun Ajaran"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && refetch()}
            />
            <InputRightElement
              children={
                <IconButton size="sm" onClick={refetch} icon={<MdSearch />} />
              }
            />
          </InputGroup> */}
            <Select
              ml={3}
              w="max"
              size="sm"
              rounded="base"
              onChange={(e) => setLimit(e.target.value)}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="400">400</option>
              <option value="600">600</option>
              <option value="800">800</option>
            </Select>
          </Flex>
        </GridItem>
        <GridItem colSpan={{ base: 8, md: 4 }}>
          <Flex justify="end" alignItems="flex-end" height="100%">
            <Button
              bgColor="cyan.700"
              color="white"
              variant="solid"
              size="sm"
              onClick={setCreateModal}
            >
              Tambah Tahun Ajaran
            </Button>
          </Flex>
        </GridItem>
      </Grid>
      <Box mt={3} borderColor="gray.200" borderWidth={1}>
        {status !== 'error' ? (
          <>
            <DataTable
              isLoaded={!isFetching}
              columns={columns}
              data={response?.data?.docs || []}
            />
            {status === 'success' && (
              <Box px={6}>
                <Pagination
                  currentPage={page}
                  pageCount={response?.data?.paginator?.pageCount || 0}
                  totalItems={response?.data?.paginator?.itemCount || 0}
                  perPage={response?.data?.paginator?.perPage || 0}
                  maxVisible={3}
                  limit={limit}
                  gotoPage={(p) => {
                    setPage(p);
                  }}
                  previousPage={() => {
                    setPage((p) => p - 1);
                  }}
                  nextPage={() => {
                    setPage((p) => p + 1);
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <>
            {errorMessage.code === 404 ? (
              <Box
                p={8}
                textAlign="center"
                bg="gray.50"
                borderRadius="md"
                mx={4}
                my={4}
              >
                <Text fontSize="lg" fontWeight="medium" color="gray.600" mb={2}>
                  Tahun ajaran tidak ditemukan
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Belum ada data tahun ajaran yang tersedia
                </Text>
              </Box>
            ) : (
              <ErrorAlert message={errorMessage.message} onRetry={refetch} />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default AcademicYearTable;
